import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/basedata/apiCall';
import { navigateToError } from '@/helpers/navigateToError';
import { getOnboardingSessionId } from '@/helpers/onboardingSession';
import { setOnboardingGuest } from '@/helpers/onboardingGuest';
import { applyCpfMask, stripCpfDigits } from '@/Pages/Signup/utils/cpfMask';
import { successNavigationOptions } from '@/Pages/Success/types/successLocationState';

const IDENTIFY_PATH = '/identify';
const MIN_DOCUMENT_DIGITS = 6;

export type UseIdentifyResult = {
  document: string;
  notFound: boolean;
  isSubmitting: boolean;
  isSubmitEnabled: boolean;
  setDocument: (value: string) => void;
  submit: () => Promise<void>;
};

/**
 * Identificação do convidado por documento (modo evento, convite COM CPF na
 * lista). Convite sem CPF usa o link único (/link/<token> → /invite). No
 * sucesso, persiste o perfil + progresso de acompanhantes e roteia:
 * onboarding concluído → /invite (tela do grupo, se houver acompanhante
 * pendente) ou /success; senão → /selfie.
 */
export function useIdentify(): UseIdentifyResult {
  const navigate = useNavigate();
  const [document, setDocumentState] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setDocument = useCallback((value: string) => {
    setDocumentState(applyCpfMask(value));
  }, []);

  const digits = stripCpfDigits(document);
  const isSubmitEnabled = digits.length >= MIN_DOCUMENT_DIGITS;

  const submit = useCallback(async (): Promise<void> => {
    const sessionId = getOnboardingSessionId();
    if (sessionId === null) {
      // Sessão perdida (storage limpo/expirado) — recomeça pelo aceite.
      navigate('/terms', { replace: true });
      return;
    }

    setIsSubmitting(true);
    setNotFound(false);

    try {
      const response = await api.onboarding.identify({
        sessionId,
        document: stripCpfDigits(document),
      });

      setOnboardingGuest({
        fullName: response.guest.fullName,
        email: response.guest.email,
        phone: response.guest.phone,
        birthDate: response.guest.birthDate,
        nationality: response.guest.nationality,
        documentDigits: stripCpfDigits(document),
        onboardingStep: response.guest.onboardingStep,
        companions: response.companions,
        role: 'titular',
        alreadyOnboarded: response.guest.alreadyOnboarded,
        enrolledAt: response.guest.enrolledAt,
      });

      if (response.guest.alreadyOnboarded) {
        // Quem já tem biometria não repete selfie/cadastro (cada passagem
        // custa liveness + indexação de face): só restam os acompanhantes
        // pendentes — ou o comprovante do que já foi feito.
        if (response.companions.pending > 0) {
          navigate('/invite');
        } else {
          navigate('/success', successNavigationOptions);
        }
        return;
      }

      navigate('/selfie', { state: { sessionId } });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setNotFound(true);
        return;
      }
      navigateToError(navigate, {
        error,
        originPath: IDENTIFY_PATH,
        ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [document, navigate]);

  return {
    document,
    notFound,
    isSubmitting,
    isSubmitEnabled,
    setDocument,
    submit,
  };
}
